import React, { useEffect } from 'react';
import { Slide, toast, ToastContainer, ToastOptions } from 'react-toastify';
import clsx from 'clsx';
/**
 * Notification component
 * Global event-triggered notification center
 * @property {number} duration Duration to keep notification on screen
 */

type Type = 'success' | 'error' | 'warning' | 'info';

interface NotificationEventDetail {
  type: Type;
  message: string;
}

interface CustomNotificationEvent extends Event {
  detail: NotificationEventDetail;
}

const NOTIFICATION_EVENT = 'app-notification';

export function notify(message: string, type: Type = 'info') {
  if (typeof window === 'undefined') {
    console.warn('notify is called on the server');
    return;
  }

  toast.clearWaitingQueue();
  toast.dismiss();

  const event: CustomNotificationEvent = new CustomEvent(NOTIFICATION_EVENT, {
    detail: { message, type },
  });
  window.dispatchEvent(event);
}

type NotificationProps = {
  duration?: number;
  className?: string;
};

export function Notification({
  duration = 3000,
  className,
}: NotificationProps) {
  const tw = {
    toast: `py-1.5! px-4! rounded-sm shadow-md! flex items-center min-h-[36px]! `,
    body: 'text-base leading-6 text-surface-3-label! items-center flex gap-x-2 font-normal',
    success: 'bg-surface-3!',
    error: 'bg-red-600!',
    info: 'bg-surface-3!',
    warning: 'bg-orange-400!',
    default: 'bg-surface-3!',
  };

  const handleChangeState = (e: CustomNotificationEvent) => {
    const options: ToastOptions = {
      position: 'bottom-center',
      autoClose: duration,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      closeButton: false,
    };

    switch (e.detail.type) {
      case 'success':
        toast.success(e.detail.message, {
          ...options,
          className: tw.toast + ' ' + tw.success,
          icon: () => <i className="fad fa-check text-surface-3-label" />,
        });
        break;
      case 'error':
        toast.error(e.detail.message, {
          ...options,
          className: tw.toast + ' ' + tw.error,
          icon: () => (
            <i className="fad fa-circle-exclamation text-surface-3-label" />
          ),
        });
        break;
      case 'warning':
        toast.warning(e.detail.message, {
          ...options,
          className: tw.toast + ' ' + tw.warning,
          icon: () => (
            <i className="fad fa-circle-exclamation text-surface-3-label" />
          ),
        });
        break;
      case 'info':
        toast.info(e.detail.message, {
          ...options,
          className: tw.toast + ' ' + tw.info,
          icon: () => (
            <i className="fas fa-check text-surface-3-label font-extrabold" />
          ),
        });
        break;
      default:
        toast.error(e.detail.message, {
          ...options,
          className: tw.toast + ' ' + tw.info,
          icon: () => (
            <i className="fas fa-check text-surface-3-label font-extrabold" />
          ),
        });
    }
  };

  useEffect(() => {
    const eventListener = (e: Event) => {
      handleChangeState(e as CustomNotificationEvent);
    };
    window.addEventListener(NOTIFICATION_EVENT, eventListener);
    return () => {
      window.removeEventListener(NOTIFICATION_EVENT, eventListener);
    };
  }, []);

  return (
    <ToastContainer
      aria-label="notifications"
      className={clsx(
        'text-surface-3-label! flex items-center gap-x-2 text-base leading-6 font-normal',
        className,
      )}
      closeButton={false}
      transition={Slide}
      draggable
      draggablePercent={60}
      toastClassName={
        'py-1.5! px-4! rounded-sm shadow-md! flex items-center min-h-[36px]! text-surface-3-label!'
      }
      limit={1}
      pauseOnHover
    />
  );
}
